import { AggregateScoreType } from "~/operations";
import { AdjustedAggregateScoreType } from ".";
import { SxProps } from "@mui/material";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";

export type Header = {
  /**
   * IDs are used to identify the column in the table
   */
  id: string;
  /**
   * Field is used to identify the column for filtering and sorting
   */
  field: string;
  /**
   * Label is the text that will be displayed in the table header
   */
  label: React.ReactNode;
  /**
   * ColSpan is used to merge columns
   * @default 1
   */
  colSpan?: number;
  /**
   * Sortable is used to determine if the column is sortable
   */
  sortable?: boolean;
  /**
   * Options is used to set the text alignment and width of the column
   */
  // TODO: roll all options into SXProps
  options?: {
    textAlign?: "inherit" | "left" | "center" | "right" | "justify";
    width?: number;
    colSpan?: number;
    sx?: SxProps;
  };
};

export type FirewatchColumnProps = {
  [key: string]: Header;
};

type FirewatchList = {
  pageType: AdjustedAggregateScoreType;
  isSmallTable?: boolean;
  isSearchResults?: boolean;
  isOrg?: boolean;
  isCVERemediation?: boolean;
  selectable?: boolean;
  isSpaceDashboard?: boolean;
  disableTakeAction?: boolean;
};

export function getFirewatchColumns({
  pageType,
  isSmallTable,
  isSearchResults,
  isOrg,
  isCVERemediation,
  selectable,
  isSpaceDashboard,
  disableTakeAction,
}: FirewatchList): Header[] {
  const { isFeatureEnabled } = useFeatureFlags();
  const isMoveTheNeedleFeatureEnabled = isFeatureEnabled("Move the needle");
  const FIREWATCH_COLUMNS: FirewatchColumnProps = {
    Select: {
      id: "SELECT",
      field: "SELECT",
      label: " ",
      sortable: false,
    },
    Rank: {
      id: "RANK",
      field: "RANK",
      label: "Rank",
      sortable: true,
      options: {
        width: 50,
      },
    },
    Impact: {
      id: "IMPACT",
      field: "RISK_VALUE", // Risk value is the appropriate field for impact component for sorting purposes
      label: "Risk",
      sortable: !isCVERemediation,
      options: { width: 72, sx: { maxWidth: 72 } },
    },
    BlastRadius: {
      id: "BLAST_RADIUS",
      field: "BLAST_RADIUS",
      label: "Blast radius",
      sortable: true,
      options: {
        sx: { pr: 0.5, whiteSpace: "nowrap", maxWidth: 100 },
      },
    },
    Vulnerability: {
      id: "VULNERABILITY",
      field: "TITLE",
      label: "Vulnerability",
      sortable: true,
    },
    Asset: {
      id: "ASSET",
      field: "TITLE",
      label: "Asset",
      sortable: true,
    },
    Check: { id: "CHECK", field: "TITLE", label: "Check", sortable: true },
    AllSecurityFinding: {
      id: "ALL_SECURITY_FINDING",
      field: "FINDING",
      label: "Finding",
      sortable: false,
    },
    TopSecurityFinding: {
      id: "TOP_SECURITY_FINDING",
      field: "FINDING",
      label: "Finding",
      sortable: false,
    },
    Control: {
      id: "CONTROL",
      field: "CONTROL",
      label: "Control",
      sortable: true,
    },
    Framework: {
      id: "FRAMEWORK",
      field: "FRAMEWORK",
      label: "Framework",
      sortable: true,
    },
    Advisory: {
      id: "ADVISORY",
      field: "TITLE",
      label: "Advisory",
    },
    AdvisoryCount: {
      id: "ADVISORY_COUNT",
      field: "ADVISORY_COUNT",
      label: "Advisories",
      sortable: false,
      options: { width: 100 },
    },
    CveCount: {
      id: "CVE_COUNT",
      field: "CVE_COUNT",
      label: "CVEs",
      sortable: false,
      options: { width: 100 },
    },
    Policy: {
      id: "POLICY",
      field: "POLICY",
      label: "Policy name",
      sortable: !isSmallTable,
    },
    Software: {
      id: "SOFTWARE",
      field: "TITLE",
      label: "Software",
      sortable: true,
    },
    RiskFactors: {
      id: "RISK_FACTORS",
      field: "RISK_FACTORS",
      label: "Risk factors",
      sortable: false,
      options: { width: 110, sx: { minWidth: 110 } },
    },
    RiskValue: {
      id: "RISK_VALUE",
      field: "RISK_VALUE",
      label: "Risk",
      sortable: true,
      options: { width: 75 },
    },
    Action: {
      id: "ACTION",
      field: "ACTION",
      label: "Action",
      sortable: false,
      options: { sx: { whiteSpace: "nowrap" } },
    },
    Space: {
      id: "SPACE",
      field: "SPACE",
      label: "Space",
      sortable: false,
    },
    Status: {
      id: "STATUS",
      field: "STATUS",
      label: "Status",
      sortable: false,
    },
    Date: {
      id: "DATE",
      field: "FIRST_FOUND",
      label: "Date",
      sortable: false,
      options: { width: isSmallTable ? 90 : 100 },
    },
    Title: {
      id: "TITLE",
      field: "TITLE",
      label: "Name",
      sortable: false,
    },
    LastUpdated: {
      id: "LAST_UPDATED",
      field: "LAST_UPDATED",
      label: "Last updated",
      sortable: false,
      options: { sx: { minWidth: 120 } },
    },
    AssetsCount: {
      id: "ASSETS_COUNT",
      field: "ASSETS_COUNT",
      label: "Assets",
      sortable: false,
      options: {
        width: 100,
      },
    },
    Type: {
      id: "TYPE",
      field: "TYPE",
      label: "Type",
      sortable: false,
    },
    TopActionsFinding: {
      id: "TOP_ACTIONS_FINDING",
      field: "FINDING",
      label: "Finding",
      options: {
        sx:
          isSmallTable && isMoveTheNeedleFeatureEnabled
            ? {
                "@media (width: 1024px) and (height: 768px)": {
                  fontSize: "10px",
                  width: 120,
                  minWidth: 120,
                  maxWidth: 120,
                },
              }
            : {},
      },
    },
    SpaceRisk: {
      id: "SPACE_RISK",
      field: "value",
      label: isSpaceDashboard ? "Δ Space Risk" : "Δ Workspace Risk",
      options: {
        width: isSpaceDashboard ? 100 : 120,
        sx: { display: "flex", whiteSpace: "nowrap" },
      },
    },
    Effort: {
      id: "EFFORT",
      field: "value",
      label: "Mondoo Effort",
      options: {
        width: 140,
        sx: {
          "@media (width: 1024px)": {
            fontSize: "10px",
            minWidth: 80,
            maxWidth: 80,
          },
        },
      },
    },
    TopActionsAssetsCount: {
      id: "TOP_ACTIONS_ASSETS_COUNT",
      field: "ASSETS_COUNT",
      label: "Assets",
      sortable: false,
      options: {
        width: 45,
      },
    },
    TriageBar: {
      id: "TRIAGE_BAR",
      field: "",
      label: "",
      sortable: false,
      options: {
        width: 200,
        sx: {
          fontSize: { xs: 11 },
          minWidth: { xs: 180 },
          maxWidth: { xs: 180 },
        },
      },
    },
    OverdueSla: {
      id: "SLA_OVERDUE_COUNT",
      field: "SLA_OVERDUE_COUNT",
      label: "Over SLA",
      sortable: true,
      options: { width: 94, sx: { minWidth: 94 } },
    },
    AtRiskSla: {
      id: "SLA_AT_RISK_COUNT",
      field: "SLA_AT_RISK_COUNT",
      label: "At Risk",
      sortable: true,
      options: { width: 88, sx: { minWidth: 88 } },
    },
  };

  const actionColumn = !disableTakeAction ? [FIREWATCH_COLUMNS.Action] : [];
  const cveColumns: Header[] = [
    ...(selectable ? [FIREWATCH_COLUMNS.Select] : []),
    FIREWATCH_COLUMNS.Impact,
    FIREWATCH_COLUMNS.BlastRadius,
    FIREWATCH_COLUMNS.Vulnerability,
    ...(!isSmallTable ? [FIREWATCH_COLUMNS.RiskFactors] : []),
    ...actionColumn,
  ].flatMap((x) => x ?? []);

  const advisoryColumns: Header[] = [
    ...(selectable ? [FIREWATCH_COLUMNS.Select] : []),
    FIREWATCH_COLUMNS.Impact,
    FIREWATCH_COLUMNS.BlastRadius,
    FIREWATCH_COLUMNS.Advisory,
    FIREWATCH_COLUMNS.RiskFactors,
    ...actionColumn,
  ].flatMap((x) => x ?? []);

  const affectedAssetsColumns: Header[] = [
    FIREWATCH_COLUMNS.Impact,
    FIREWATCH_COLUMNS.Asset,
    FIREWATCH_COLUMNS.RiskFactors,
    FIREWATCH_COLUMNS.LastUpdated,
  ].flatMap((x) => x ?? []);

  const checksColumns: Header[] = [
    ...(selectable ? [FIREWATCH_COLUMNS.Select] : []),
    FIREWATCH_COLUMNS.Impact,
    FIREWATCH_COLUMNS.BlastRadius,
    FIREWATCH_COLUMNS.Check,
    FIREWATCH_COLUMNS.Status,
    ...(!isSmallTable ? [FIREWATCH_COLUMNS.RiskFactors] : []),
    ...actionColumn,
  ].flatMap((x) => x ?? []);

  const allSecurityFindingsColumns: Header[] = [
    ...(selectable ? [FIREWATCH_COLUMNS.Select] : []),
    FIREWATCH_COLUMNS.Impact,
    FIREWATCH_COLUMNS.BlastRadius,
    FIREWATCH_COLUMNS.AllSecurityFinding,
    FIREWATCH_COLUMNS.RiskFactors,
    ...actionColumn,
  ].flatMap((x) => x ?? []);

  const assetAllFindingsColumns: Header[] = [
    ...(selectable ? [FIREWATCH_COLUMNS.Select] : []),
    FIREWATCH_COLUMNS.Impact,
    FIREWATCH_COLUMNS.AllSecurityFinding,
    FIREWATCH_COLUMNS.RiskFactors,
    ...actionColumn,
  ].flatMap((x) => x ?? []);

  const slaFindingsColumns: Header[] = [
    ...(selectable ? [FIREWATCH_COLUMNS.Select] : []),
    FIREWATCH_COLUMNS.OverdueSla,
    FIREWATCH_COLUMNS.AtRiskSla,
    FIREWATCH_COLUMNS.Impact,
    FIREWATCH_COLUMNS.AllSecurityFinding,
    FIREWATCH_COLUMNS.RiskFactors,
    ...actionColumn,
  ].flatMap((x) => x ?? []);

  const topSecurityFindingsColumns: Header[] = [
    FIREWATCH_COLUMNS.Rank,
    FIREWATCH_COLUMNS.Impact,
    FIREWATCH_COLUMNS.BlastRadius,
    FIREWATCH_COLUMNS.TopSecurityFinding,
    FIREWATCH_COLUMNS.FirstFound,
  ].flatMap((x) => x ?? []);

  const topActionsRiskReductionColumns: Header[] = [
    FIREWATCH_COLUMNS.Rank,
    FIREWATCH_COLUMNS.SpaceRisk,
    FIREWATCH_COLUMNS.TopActionsFinding,
    FIREWATCH_COLUMNS.TopActionsAssetsCount,
    FIREWATCH_COLUMNS.Effort,
    FIREWATCH_COLUMNS.TriageBar,
  ].flatMap((x) => x ?? []);

  const controlsColumns: Header[] = [
    ...(isSearchResults ? [] : [FIREWATCH_COLUMNS.Rank]),
    FIREWATCH_COLUMNS.Impact,
    FIREWATCH_COLUMNS.BlastRadius,
    FIREWATCH_COLUMNS.Control,
    FIREWATCH_COLUMNS.RiskFactors,
  ].flatMap((x) => x ?? []);

  const frameworksColumns: Header[] = [
    ...(isSearchResults ? [] : [FIREWATCH_COLUMNS.Rank]),
    FIREWATCH_COLUMNS.Impact,
    FIREWATCH_COLUMNS.BlastRadius,
    FIREWATCH_COLUMNS.Framework,
    FIREWATCH_COLUMNS.RiskFactors,
  ].flatMap((x) => x ?? []);

  const policyColumns: Header[] = [
    ...(isSearchResults ? [] : [FIREWATCH_COLUMNS.Rank]),
    FIREWATCH_COLUMNS.Impact,
    FIREWATCH_COLUMNS.BlastRadius,
    FIREWATCH_COLUMNS.Policy,
  ].flatMap((x) => x ?? []);

  const softwareColumns: Header[] = [
    ...(isSearchResults ? [] : [FIREWATCH_COLUMNS.Rank]),
    FIREWATCH_COLUMNS.Impact,
    FIREWATCH_COLUMNS.BlastRadius,
    FIREWATCH_COLUMNS.Software,
    FIREWATCH_COLUMNS.RiskFactors,
  ];

  const versionedSoftwareColumns: Header[] = [
    FIREWATCH_COLUMNS.Rank,
    FIREWATCH_COLUMNS.Impact,
    FIREWATCH_COLUMNS.BlastRadius,
    FIREWATCH_COLUMNS.Software,
    FIREWATCH_COLUMNS.AssetsCount,
  ];

  const newestRelevantVulnerabilitiesColumns: Header[] = [
    FIREWATCH_COLUMNS.Date,
    FIREWATCH_COLUMNS.Impact,
    FIREWATCH_COLUMNS.Vulnerability,
  ].flatMap((x) => x ?? []);

  const searchAllColumns: Header[] = [
    FIREWATCH_COLUMNS.Impact,
    FIREWATCH_COLUMNS.Type,
    FIREWATCH_COLUMNS.Title,
    ...(isOrg ? [FIREWATCH_COLUMNS.Space] : []),
    FIREWATCH_COLUMNS.RiskFactors,
  ].flatMap((x) => x ?? []);

  const remediationWorkflowFindingsColumns: Header[] = [
    FIREWATCH_COLUMNS.SpaceRisk,
    FIREWATCH_COLUMNS.TopActionsFinding,
    FIREWATCH_COLUMNS.TopActionsAssetsCount,
  ].flatMap((x) => x ?? []);

  switch (pageType) {
    case "ALL":
      return searchAllColumns;
    case "NewestRelevantVulnerabilities":
      return newestRelevantVulnerabilitiesColumns;
    case "AllSecurityFindings":
      return allSecurityFindingsColumns;
    case "AssetAllFindings":
      return assetAllFindingsColumns;
    case "SlaFindings":
      return slaFindingsColumns;
    case "TopSecurityFindings":
      return topSecurityFindingsColumns;
    case "TopActionsRiskReduction":
      return topActionsRiskReductionColumns;
    case "RemediationWorkflowFindings":
      return remediationWorkflowFindingsColumns;
    case AggregateScoreType.Advisory:
      return advisoryColumns;
    case AggregateScoreType.Asset:
      return affectedAssetsColumns;
    case AggregateScoreType.Check:
      return checksColumns;
    case AggregateScoreType.Control:
      return controlsColumns;
    case AggregateScoreType.Framework:
      return frameworksColumns;
    case AggregateScoreType.Vulnerability:
      return cveColumns;
    case AggregateScoreType.Policy:
      return policyColumns;
    case AggregateScoreType.Software:
      return softwareColumns;
    case AggregateScoreType.VersionedSoftware:
      return versionedSoftwareColumns;
    default:
      return [];
  }
}
